// 🎯 DEBOUNCE SERVICE - Evita múltiples requests simultáneos
// Optimiza el rendimiento y reduce la carga del servidor

export interface DebounceConfig {
  delay: number;
  maxWait?: number;
  leading?: boolean;
  trailing?: boolean;
}

export interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
}

export class DebounceService {
  private timers = new Map<string, NodeJS.Timeout>();
  private lastCallTimes = new Map<string, number>();

  /**
   * Crea una función debounced que se ejecuta después del delay
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number,
    options: Partial<DebounceConfig> = {}
  ): DebouncedFunction<T> {
    const { leading = false, trailing = true, maxWait } = options;
    let timeoutId: NodeJS.Timeout | null = null;
    let lastCallTime = 0;
    let lastArgs: Parameters<T> | null = null;

    const debounced = (...args: Parameters<T>) => {
      const now = Date.now();
      lastArgs = args;

      if (leading && now - lastCallTime >= delay) {
        lastCallTime = now;
        func(...args);
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        if (trailing && lastArgs) {
          lastCallTime = Date.now();
          func(...lastArgs);
        }
        timeoutId = null;
        lastArgs = null;
      }, delay);

      // Max wait logic
      if (maxWait && now - lastCallTime >= maxWait) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        lastCallTime = now;
        func(...args);
        timeoutId = null;
        lastArgs = null;
      }
    };

    debounced.cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
        lastArgs = null;
      }
    };

    debounced.flush = () => {
      if (timeoutId && lastArgs) {
        clearTimeout(timeoutId);
        lastCallTime = Date.now();
        func(...lastArgs);
        timeoutId = null;
        lastArgs = null;
      }
    };

    return debounced;
  }

  /**
   * Debounce específico para API calls
   */
  debounceApiCall<T extends (...args: any[]) => Promise<any>>(
    apiCall: T,
    delay: number = 300
  ): DebouncedFunction<T> {
    return this.debounce(apiCall, delay, {
      leading: false,
      trailing: true,
      maxWait: 1000, // Máximo 1 segundo de espera
    });
  }

  /**
   * Debounce para interacciones de usuario (botones, inputs)
   */
  debounceUserInteraction<T extends (...args: any[]) => any>(
    func: T,
    delay: number = 250
  ): DebouncedFunction<T> {
    return this.debounce(func, delay, {
      leading: true, // Ejecuta inmediatamente en el primer clic
      trailing: false, // No ejecuta al final
    });
  }

  /**
   * Debounce para búsquedas en tiempo real
   */
  debounceSearch<T extends (...args: any[]) => any>(
    searchFunc: T,
    delay: number = 500
  ): DebouncedFunction<T> {
    return this.debounce(searchFunc, delay, {
      leading: false,
      trailing: true,
      maxWait: 2000, // Máximo 2 segundos para búsquedas
    });
  }

  /**
   * Limpia todos los timers pendientes
   */
  clearAll(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.lastCallTimes.clear();
  }

  /**
   * Obtiene estadísticas de debouncing
   */
  getStats(): {
    activeTimers: number;
    lastCallTimes: Map<string, number>;
  } {
    return {
      activeTimers: this.timers.size,
      lastCallTimes: this.lastCallTimes,
    };
  }
}

// 🎯 INSTANCIA GLOBAL DEL DEBOUNCE SERVICE
export const debounceService = new DebounceService();

// 🎯 FUNCIONES UTILITARIAS EXPORTADAS
export const debounce = debounceService.debounce.bind(debounceService);
export const debounceApiCall = debounceService.debounceApiCall.bind(debounceService);
export const debounceUserInteraction = debounceService.debounceUserInteraction.bind(debounceService);
export const debounceSearch = debounceService.debounceSearch.bind(debounceService); 
